import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, saveBlob } from '../lib/api.ts';
import { day, money } from '../lib/format.ts';
import { useAction, useSave } from './manage/common.tsx';
import { StatementDialog } from './Statement.tsx';

/**
 * Following up what is owed (modules/invoicing followup.ts): how old the open amounts are, per
 * customer or supplier, and payment reminders in up to three steps, e-mailed or printed.
 */

export const AGES = ['current', 'd30', 'd60', 'd90', 'older'] as const;
type Ages = Record<(typeof AGES)[number], number>;
interface AgedRow {
  name: string;
  buckets: Ages;
  total: number;
  late: number;
}
export interface AgedData<R extends AgedRow = AgedRow> {
  today: string;
  rows: R[];
  totals: { buckets: Ages; total: number };
}

/** The ages table: one row per customer or supplier, by how late what is open is. */
export function AgedTable<R extends AgedRow>({ data, who, label, onOpen }: { data: AgedData<R>; who: string; label: string; onOpen?: (r: R) => void }) {
  const csv = () => {
    const cell = (v: string | number) => (typeof v === 'number' ? (v / 100).toFixed(2) : `"${v.replace(/"/g, '""')}"`);
    const rows = [
      [who, ...AGES.map((a) => t(`st.a_${a}`)), t('inv.total')].map(cell),
      ...data.rows.map((r) => [cell(r.name), ...AGES.map((a) => cell(r.buckets[a])), cell(r.total)]),
      [cell(t('inv.total')), ...AGES.map((a) => cell(data.totals.buckets[a])), cell(data.totals.total)],
    ];
    void saveBlob(new Blob([`﻿${rows.map((r) => r.join(',')).join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' }), `${label.replace(/[^\p{L}\p{N}]+/gu, '-')}-${data.today}.csv`);
  };
  if (!data.rows.length) return <EmptyState icon="checkCircle" title={t('fu.nothing_open')} />;
  return (
    <>
      <div className="table-wrap">
        <table className="data-table aged-table" aria-label={label}>
          <thead>
            <tr>
              <th className="left">{who}</th>
              {AGES.map((a) => (
                <th key={a}>{t(`st.a_${a}`)}</th>
              ))}
              <th>{t('inv.total')}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i}>
                <td className="left">
                  {onOpen ? (
                    <button className="link-button" onClick={() => onOpen(r)}>
                      {r.name}
                    </button>
                  ) : (
                    r.name
                  )}
                </td>
                {AGES.map((a) => (
                  <td key={a} className={a !== 'current' && r.buckets[a] > 0 ? (a === 'd30' ? 'warn' : 'danger') : r.buckets[a] ? '' : 'muted'}>
                    {r.buckets[a] ? money(r.buckets[a]) : '–'}
                  </td>
                ))}
                <td>
                  <strong>{money(r.total)}</strong>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th className="left">{t('inv.total')}</th>
              {AGES.map((a) => (
                <th key={a}>{money(data.totals.buckets[a])}</th>
              ))}
              <th>{money(data.totals.total)}</th>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="row-actions">
        <Button size="sm" icon="download" onClick={csv}>
          {t('st.csv')}
        </Button>
      </div>
    </>
  );
}

interface FollowRow {
  customer: string;
  name: string;
  email: string | null;
  invoices: { id: string; name: string; date: string; due_date: string; open: number; days: number; reminded: number }[];
  overdue: number;
  oldest: number;
  level_due: number;
  last: { level: number; state: string; channel: string; at: string } | null;
}
interface ReminderRow {
  id: string;
  customer: string;
  customer_name: string;
  level: number;
  total: number;
  channel: 'email' | 'print';
  to_email: string | null;
  subject: string;
  state: 'queued' | 'sent' | 'failed' | 'printed' | 'skipped';
  error: string | null;
  sent_at: string | null;
  automatic: boolean;
  created_at: string;
  invoices: number;
}
interface ReminderFull {
  id: string;
  subject: string;
  body: string;
  channel: 'email' | 'print';
  to_email: string | null;
  state: ReminderRow['state'];
  created_at: string;
}

const STATE_TONE: Record<ReminderRow['state'], Tone> = { queued: 'info', sent: 'ok', printed: 'neutral', failed: 'danger', skipped: 'warn' };

/** The follow-up tab of Invoices: ages of what customers owe, and payment reminders. */
export function FollowUpTab({ canRemind }: { canRemind: boolean }) {
  const { data: aged } = useAction<AgedData<AgedRow & { customer: string }>>('invoicing.aged');
  const [statement, setStatement] = useState<{ customer: string; name: string } | null>(null);
  return (
    <div className="form-stack">
      <section className="panel" aria-labelledby="fu-aged">
        <div className="panel-head">
          <div>
            <h2 id="fu-aged">{t('fu.aged_title')}</h2>
            <p className="muted">{t('fu.aged_hint')}</p>
          </div>
        </div>
        {!aged ? <Skeleton h={160} /> : <AgedTable data={aged} who={t('inv.customer')} label={t('fu.aged_title')} onOpen={(r) => setStatement({ customer: r.customer, name: r.name })} />}
      </section>
      {canRemind ? <Reminders /> : null}
      {statement ? <StatementDialog customer={statement.customer} name={statement.name} onClose={() => setStatement(null)} /> : null}
    </div>
  );
}

function Reminders() {
  const { data, reload } = useAction<{ today: string; levels: { days: number }[]; rows: FollowRow[] }>('invoicing.followups');
  const { data: log, reload: reloadLog } = useAction<ReminderRow[]>('invoicing.reminders');
  const { busy, run } = useSave();
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [printing, setPrinting] = useState<ReminderFull[] | null>(null);
  const rows = data?.rows ?? [];
  const chosen = picked ?? new Set(rows.filter((r) => r.level_due > 0).map((r) => r.customer));
  const toggle = (id: string, on: boolean) => {
    const next = new Set(chosen);
    if (on) next.add(id);
    else next.delete(id);
    setPicked(next);
  };
  const send = () =>
    void run(
      async () => {
        const made = await action<ReminderFull[]>('invoicing.send_reminders', { customers: [...chosen] });
        setPicked(null);
        await Promise.all([reload(), reloadLog()]);
        const paper = made.filter((r) => r.channel === 'print');
        if (paper.length) printLetters(paper);
      },
      tn('fu.sent', chosen.size),
    );
  const printLetters = (letters: ReminderFull[]) => {
    setPrinting(letters);
    setTimeout(() => {
      window.print();
      setPrinting(null);
    }, 50);
  };
  return (
    <>
      <section className="panel" aria-labelledby="fu-remind">
        <div className="panel-head">
          <div>
            <h2 id="fu-remind">{t('fu.remind_title')}</h2>
            <p className="muted">{t('fu.remind_hint')}</p>
          </div>
          <Button variant="primary" icon="send" onClick={send} busy={busy} disabled={!chosen.size}>
            {chosen.size ? tn('fu.send_n', chosen.size) : t('fu.send')}
          </Button>
        </div>
        {!data ? (
          <Skeleton h={120} />
        ) : !rows.length ? (
          <EmptyState icon="checkCircle" title={t('fu.none_overdue')} />
        ) : (
          <ul className="fu-list">
            {rows.map((r) => (
              <li key={r.customer}>
                <label className="fu-row">
                  <input type="checkbox" checked={chosen.has(r.customer)} onChange={(e) => toggle(r.customer, e.target.checked)} aria-label={t('fu.pick', { name: r.name })} />
                  <span className="list-main">
                    <span className="list-title">{r.name}</span>
                    <span className="list-sub">
                      {[
                        tn('fu.invoices_n', r.invoices.length),
                        tn('fu.oldest', r.oldest),
                        r.email ?? t('fu.no_email'),
                        r.last ? t('fu.last', { level: r.last.level, date: day(r.last.at.slice(0, 10)) }) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <strong className="fu-amount">{money(r.overdue)}</strong>
                  {r.level_due ? (
                    <StatusBadge tone={r.level_due >= 3 ? 'danger' : 'warn'} icon="bell" label={t('fu.step_due', { n: r.level_due })} />
                  ) : (
                    <StatusBadge tone="neutral" icon={r.last ? 'check' : 'clock'} label={t(r.last ? 'fu.reminded' : 'fu.too_soon')} />
                  )}
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="panel" aria-labelledby="fu-log">
        <div className="panel-head">
          <h2 id="fu-log">{t('fu.log_title')}</h2>
        </div>
        {!log ? (
          <Skeleton h={80} />
        ) : !log.length ? (
          <EmptyState icon="mail" title={t('fu.log_empty')} />
        ) : (
          <div className="list-card">
            {log.map((r) => (
              <button key={r.id} className="list-row" onClick={() => setOpen(r.id)}>
                <span className="list-icon" aria-hidden="true">
                  <Icon name={r.channel === 'email' ? 'mail' : 'printer'} size={18} />
                </span>
                <span className="list-main">
                  <span className="list-title">{r.customer_name}</span>
                  <span className="list-sub">
                    {[t('fu.step', { n: r.level }), money(r.total), r.to_email, day(r.created_at.slice(0, 10)), r.automatic ? t('fu.automatic') : null, r.error].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <StatusBadge
                  tone={STATE_TONE[r.state]}
                  icon={r.state === 'sent' ? 'check' : r.state === 'failed' ? 'alert' : r.state === 'printed' ? 'printer' : 'clock'}
                  label={t(`fu.st_${r.state}` as MessageKey)}
                />
              </button>
            ))}
          </div>
        )}
      </section>
      {open ? <ReminderDialog id={open} onClose={() => setOpen(null)} onPrint={(r) => printLetters([r])} /> : null}
      {printing
        ? createPortal(
            <div className="print-sheet">
              {printing.map((r) => (
                <ReminderLetter key={r.id} r={r} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function ReminderLetter({ r }: { r: ReminderFull }) {
  return (
    <article className="reminder-letter">
      <p className="muted">{day(r.created_at.slice(0, 10))}</p>
      <h2>{r.subject}</h2>
      <p className="reminder-body">{r.body}</p>
    </article>
  );
}

function ReminderDialog({ id, onClose, onPrint }: { id: string; onClose: () => void; onPrint: (r: ReminderFull) => void }) {
  const { data } = useAction<ReminderFull>('invoicing.reminder', { reminder: id });
  return (
    <Dialog
      title={data?.subject ?? t('fu.reminder')}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="primary" icon="printer" onClick={() => data && onPrint(data)} disabled={!data}>
          {t('inv.print')}
        </Button>
      }
    >
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <div className="form-stack">
          <p className="muted">{data.channel === 'email' ? t('fu.to', { email: data.to_email ?? '' }) : t('fu.paper')}</p>
          <div className="inv-preview">
            <ReminderLetter r={data} />
          </div>
        </div>
      )}
    </Dialog>
  );
}

export interface ReminderLevel {
  days: number;
  subject: string;
  message: string;
}
interface DraftLevel {
  key: string;
  days: string;
  subject: string;
  message: string;
}
export const draftLevels = (levels: ReminderLevel[]): DraftLevel[] => levels.map((l, i) => ({ key: `l${i}`, days: String(l.days), subject: l.subject, message: l.message }));
export const savedLevels = (draft: DraftLevel[]): ReminderLevel[] => draft.map((l) => ({ days: Number(l.days), subject: l.subject.trim(), message: l.message.trim() }));
export function levelsError(draft: DraftLevel[]): string | null {
  for (const [i, l] of draft.entries()) {
    if (!/^\d{1,3}$/.test(l.days.trim()) || Number(l.days) < 1) return t('fu.days_rule');
    if (l.subject.trim().length < 3 || l.message.trim().length < 10) return t('fu.text_rule');
    if (i > 0 && Number(l.days) <= Number(draft[i - 1]!.days)) return t('fu.order_rule');
  }
  return null;
}

/** The reminder steps and whether they go out by themselves, in the invoicing settings. */
export function ReminderSettings({ levels, onLevels, auto, onAuto }: { levels: DraftLevel[]; onLevels: (l: DraftLevel[]) => void; auto: boolean; onAuto: (on: boolean) => void }) {
  const set = (key: string, patch: Partial<DraftLevel>) => onLevels(levels.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const error = levelsError(levels);
  return (
    <fieldset className="option-group">
      <legend className="field-label">
        {t('fu.settings_title')} <span className="muted">{t('fu.settings_hint')}</span>
      </legend>
      {levels.map((l, i) => (
        <div key={l.key} className="fu-level">
          <div className="fu-level-head">
            <strong>{t('fu.step', { n: i + 1 })}</strong>
            <span className="input-unit">
              <input className="input" inputMode="numeric" maxLength={3} value={l.days} aria-label={t('fu.days_of', { n: i + 1 })} onChange={(e) => set(l.key, { days: e.target.value })} />
              <span>{t('fu.days_after')}</span>
            </span>
            <IconButton icon="trash" label={t('fu.remove_step', { n: i + 1 })} onClick={() => onLevels(levels.filter((x) => x.key !== l.key))} />
          </div>
          <Field label={t('fu.subject')}>
            <input className="input" maxLength={120} value={l.subject} onChange={(e) => set(l.key, { subject: e.target.value })} />
          </Field>
          <Field label={t('fu.message')} hint={t('fu.message_hint')}>
            <textarea className="input textarea" rows={3} maxLength={2000} value={l.message} onChange={(e) => set(l.key, { message: e.target.value })} />
          </Field>
        </div>
      ))}
      <div className="row-actions">
        <Button
          size="sm"
          icon="plus"
          disabled={levels.length >= 3}
          onClick={() => onLevels([...levels, { key: `n${Date.now()}`, days: String((Number(levels[levels.length - 1]?.days) || 0) + 14), subject: '', message: '' }])}
        >
          {t('fu.add_step')}
        </Button>
      </div>
      <div className="switch-row">
        <span>
          <span className="switch-title">{t('fu.auto')}</span>
          <span className="muted small">{t('fu.auto_hint')}</span>
        </span>
        <Switch checked={auto} label={t('fu.auto')} onChange={onAuto} />
      </div>
      {error ? (
        <p className="field-msg" role="alert">
          <Icon name="alert" size={14} /> {error}
        </p>
      ) : null}
    </fieldset>
  );
}
